import {Component, OnInit, OnDestroy, ViewChild, TemplateRef} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {ChangeData, CountryISO} from 'ngx-intl-tel-input';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {catchError, exhaustMap, filter, map, switchMap} from 'rxjs/operators';
import {ErrorService} from '../shared/services/error.service';
import {detectUserAgent, notEmpty, notNull, phoneNumberValidator, UserAgent} from '../shared/utils';
import {ToastrService} from 'ngx-toastr';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {HttpClient} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {PhoneNumberUtil, PhoneNumberType} from 'google-libphonenumber';

interface AddFanMutationInput {
  uuid: string;
  username: string;
}
interface Inviter {
  username: string;
  first_name: string;
  last_name: string;
}

interface Fanship {
  id: number;
  supported_client: number;
  fan: number;
  detail?: string; // Error details
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit, OnDestroy {
  @ViewChild('registrationModal', {static: false}) registrationModal!: TemplateRef<never>;
  uuid: string | null = null;
  inviter: Inviter | null = null;
  form = new FormGroup({
    phoneNumber: new FormControl(null, [Validators.required, phoneNumberValidator]),
  });
  showError = false;
  submit$ = new Subject<AddFanMutationInput>();
  bsModalRef: BsModalRef | null = null;
  userAgent: UserAgent | null = null;
  UserAgent = UserAgent;
  CountryISO = CountryISO;
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    const uuid$ = this.route.params.pipe(
      map(params => params.uuid),
      filter(notEmpty),
    );

    this.subscriptions.push(
      uuid$.subscribe(uuid => (this.uuid = uuid)),

      uuid$
        .pipe(
          switchMap(uuid =>
            this.http.get<Inviter>(`${environment.host}api/register/${uuid}`).pipe(
              catchError(error => {
                this.errorHandler.handleRESTError(error);
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe(inviter => (this.inviter = inviter)),

      this.form.valueChanges.subscribe(() => (this.showError = false)),

      this.submit$
        .pipe(
          exhaustMap(({username, uuid}) =>
            this.http
              .post<Fanship>(`${environment.host}api/register-username/${uuid}`, {username})
              .pipe(
                catchError(error => {
                  this.errorHandler.handleRESTError(error);
                  return EMPTY;
                }),
              ),
          ),
        )
        .subscribe(response => {
          if (response.supported_client && response.fan) {
            this.showModal();
          }
        }),
    );
    this.userAgent = detectUserAgent();
  }

  onSubmit(): void {
    this.form.updateValueAndValidity();
    if (this.form.invalid) {
      this.showError = true;
      return;
    }

    const username = (this.form.controls['phoneNumber'].value as ChangeData).e164Number;
    if (!username) {
      this.toastr.show('The phone number cannot be parsed. Please try again.');
      return;
    }

    if (this.isValidMobileNumber(username) === false) {
      this.showError = true;
      return;
    }

    this.submit$.next({
      username: username,
      uuid: notNull(this.uuid),
    });
  }

  closeModalAndRedirect(): void {
    this.bsModalRef?.hide();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private isValidMobileNumber(phoneNumber: string): boolean {
    const phoneNumberUtil = PhoneNumberUtil.getInstance();
    const parsed = phoneNumberUtil.parse(phoneNumber);
    return phoneNumberUtil.getNumberType(parsed) === PhoneNumberType.MOBILE;
  }

  private showModal(): void {
    this.bsModalRef = this.modalService.show(this.registrationModal, {
      class: 'modal-dialog-centered',
    });
    this.subscriptions.push(
      this.bsModalRef.onHide.subscribe(() => {
        if (this.userAgent === UserAgent.ANDROID) {
          location.href = 'https://play.google.com/store/apps/details?id=com.vasio.iamokay';
          return;
        }
        if (this.userAgent === UserAgent.IOS) {
          location.href = 'https://apps.apple.com/app/im-okay/id1597911224';
          return;
        }
      }),
    );
  }
}
